import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Group, Matrix4, Quaternion, ShapeGeometry, Vector3 } from 'three';

import { context } from '@react-three/drei/web/pivotControls/context.js';
import { ThreeEvent, useThree } from '@react-three/fiber';

import TriangleShape from './TriangleShape.js';

const vec1 = new Vector3();
const vec2 = new Vector3();

export const calculateOffset = (
  clickPoint: Vector3,
  normal: Vector3,
  rayStart: Vector3,
  rayDir: Vector3
) => {
  const e1 = normal.dot(normal);
  const e2 = normal.dot(clickPoint) - normal.dot(rayStart);
  const e3 = normal.dot(rayDir);

  if (e3 === 0) {
    return -e2 / e1;
  }

  vec1
    .copy(rayDir)
    .multiplyScalar(e1 / e3)
    .sub(normal);
  vec2
    .copy(rayDir)
    .multiplyScalar(e2 / e3)
    .add(rayStart)
    .sub(clickPoint);

  const offset = -vec1.dot(vec2) / vec1.dot(vec1);
  return offset;
};

const upV = new Vector3(0, 1, 0);
const offsetMatrix = new Matrix4();

export const AxisArrow: React.FC<{ direction: Vector3; axis: 0 | 1 | 2 }> = ({
  direction,
  axis,
}) => {
  const {
    translation,
    translationLimits,
    depthTest,
    scale,
    lineWidth,
    fixed,
    axisColors,
    hoveredColor,
    opacity,
    onDragStart,
    onDrag,
    onDragEnd,
    userData,
  } = useContext(context);

  // @ts-expect-error new in @react-three/fiber@7.0.5
  const camControls = useThree((state) => state.controls) as { enabled: boolean };

  const objRef = useRef<Group>(null!);
  const clickInfo = useRef<{ clickPoint: Vector3; dir: Vector3 } | null>(null);
  const offset0 = useRef<number>(0);
  const [isHovered, setIsHovered] = useState(false);

  const onPointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      const rotation = new Matrix4().extractRotation(objRef.current.matrixWorld);
      const clickPoint = e.point.clone();
      const origin = new Vector3().setFromMatrixPosition(objRef.current.matrixWorld);
      const dir = direction.clone().applyMatrix4(rotation).normalize();
      clickInfo.current = { clickPoint, dir };
      offset0.current = translation.current[axis];
      onDragStart({ component: 'Arrow', axis, origin, directions: [dir] });
      camControls && (camControls.enabled = false);
      // @ts-ignore - setPointerCapture is not in the type definition
      e.target.setPointerCapture(e.pointerId);
    },
    [direction, camControls, onDragStart, translation, axis]
  );

  const onPointerMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      if (!isHovered) setIsHovered(true);

      if (clickInfo.current) {
        const { clickPoint, dir } = clickInfo.current;
        const [min, max] = translationLimits?.[axis] || [undefined, undefined];

        let offset = calculateOffset(clickPoint, dir, e.ray.origin, e.ray.direction);
        if (min !== undefined) {
          offset = Math.max(offset, min - offset0.current);
        }
        if (max !== undefined) {
          offset = Math.min(offset, max - offset0.current);
        }
        translation.current[axis] = offset0.current + offset;

        offsetMatrix.makeTranslation(dir.x * offset, dir.y * offset, dir.z * offset);
        onDrag(offsetMatrix);
      }
    },
    [onDrag, isHovered, translation, translationLimits, axis]
  );

  const onPointerUp = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      clickInfo.current = null;
      onDragEnd();
      camControls && (camControls.enabled = true);
      // @ts-ignore - releasePointerCapture & PointerEvent#pointerId is not in the type definition
      e.target.releasePointerCapture(e.pointerId);
    },
    [camControls, onDragEnd]
  );

  const onPointerOut = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setIsHovered(false);
  }, []);

  const { triangleWidth, triangleHeight, matrixL } = useMemo(() => {
    const triangleWidth = fixed ? (lineWidth / scale) * 1.6 : scale / 5;
    const triangleHeight = fixed ? 0.2 : scale / 5;
    const triangleLength = fixed ? 1 - triangleHeight : scale - triangleHeight;
    const position = new Vector3(0, triangleLength + triangleHeight / 2.0, 0);
    const matrixL = new Matrix4();
    const quaternion = new Quaternion().setFromUnitVectors(upV, direction.clone().normalize());

    matrixL.setPosition(position);
    matrixL.multiplyMatrices(new Matrix4().makeRotationFromQuaternion(quaternion), matrixL);
    matrixL.lookAt(new Vector3(0, 0, 0), direction, new Vector3(0, 1, 0));

    return { triangleWidth, triangleHeight, matrixL };
  }, [fixed, lineWidth, scale, direction]);

  const color_ = isHovered ? hoveredColor : '#737373';

  const triangleGeometry = useMemo(
    () => new ShapeGeometry(new TriangleShape(triangleWidth, triangleHeight)),
    [triangleHeight, triangleWidth]
  );
  return (
    <group ref={objRef}>
      <group
        matrix={matrixL}
        matrixAutoUpdate={false}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerOut={onPointerOut}
        onPointerUp={onPointerUp}
      >
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <primitive object={triangleGeometry} />
          <meshBasicMaterial
            polygonOffset
            transparent
            color={color_}
            depthTest={depthTest}
            fog={false}
            opacity={isHovered ? 1.0 : opacity}
            polygonOffsetFactor={-10}
          />
        </mesh>
      </group>
    </group>
  );
};

export default AxisArrow;
